import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MetricCard } from '@/components/MetricCard';
import { BarChart, LineChart, ChartContainer } from '@/components/charts';
import {
  useDealsByStage,
  useDealsByType,
  useDealTrends,
  useTopDeals,
  useDealsClosingSoon,
  useDealPipelines,
  usePeriodFilter,
  useDealSummaryWithComparison,
} from '../hooks';
import { PeriodSelector } from '../components/PeriodSelector';
import { formatDateRange } from '../utils/periodUtils';
import { getSalesManagers } from '@/features/accounts/api';
import {
  RefreshCw,
  DollarSign,
  TrendingUp,
  Trophy,
  Target,
  Clock,
  Building2,
  Calendar,
  Percent,
  Users,
} from 'lucide-react';

const DEAL_TYPE_LABELS: Record<string, string> = {
  expansion: 'Expansion',
  upgrade: 'Upgrade',
  'cross-sell': 'Cross-sell',
  renewal: 'Renewal',
  other: 'Other',
};

const STAGE_COLORS = [
  '#3498db', // Blue
  '#2ecc71', // Green
  '#f1c40f', // Yellow
  '#e74c3c', // Red
  '#9b59b6', // Purple
  '#1abc9c', // Teal
  '#e67e22', // Orange
  '#34495e', // Dark gray
];

const TYPE_COLORS = {
  expansion: '#27ae60',
  upgrade: '#3498db',
  'cross-sell': '#9b59b6',
  renewal: '#f39c12',
  other: '#95a5a6',
};

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '--';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function DealInsightsPage() {
  const queryClient = useQueryClient();
  const [selectedPipeline, setSelectedPipeline] = useState<string | undefined>(undefined);
  const [salesManagerFilter, setSalesManagerFilter] = useState<string>('all');
  const [initialized, setInitialized] = useState(false);

  // Fetch sales managers for filter dropdown
  const { data: salesManagersData } = useQuery({
    queryKey: ['sales-managers'],
    queryFn: getSalesManagers,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const salesManagers = salesManagersData || [];

  // Period filtering with comparison support
  const {
    preset,
    customRange,
    comparisonEnabled,
    currentRange,
    previousRange,
    currentFilters,
    previousFilters,
    setPreset,
    setCustomRange,
    setComparisonEnabled,
  } = usePeriodFilter('this_month');

  const { data: pipelines, isLoading: pipelinesLoading } = useDealPipelines();

  // Auto-select first pipeline once loaded (better UX than showing all 23 stages)
  if (!initialized && !pipelinesLoading && pipelines && pipelines.length > 0 && !selectedPipeline) {
    setSelectedPipeline(pipelines[0].id);
    setInitialized(true);
  }

  // Build filters with date range
  const dealFilters = {
    pipeline: selectedPipeline,
    dateFrom: currentFilters.dateFrom,
    dateTo: currentFilters.dateTo,
    salesManagerId: salesManagerFilter === 'all' ? undefined : salesManagerFilter,
  };

  const previousDealFilters = {
    pipeline: selectedPipeline,
    dateFrom: previousFilters.dateFrom,
    dateTo: previousFilters.dateTo,
    salesManagerId: salesManagerFilter === 'all' ? undefined : salesManagerFilter,
  };

  // Use summary with comparison hook
  const {
    data: summaryData,
    isLoading: summaryLoading,
    isFetching,
  } = useDealSummaryWithComparison(dealFilters, comparisonEnabled, previousDealFilters);

  const summary = summaryData?.current;
  const changes = summaryData?.changes;

  const { data: byStage, isLoading: stageLoading } = useDealsByStage(selectedPipeline);
  const { data: byType, isLoading: typeLoading } = useDealsByType(selectedPipeline);

  // Map preset to trend period format
  const trendPeriod = preset.includes('week') ? 'week' : preset.includes('quarter') ? 'quarter' : 'month';
  const { data: trends, isLoading: trendsLoading } = useDealTrends(trendPeriod, selectedPipeline);

  const { data: topDeals, isLoading: topLoading } = useTopDeals(10, selectedPipeline);
  const { data: closingSoon, isLoading: closingLoading } = useDealsClosingSoon(30, selectedPipeline);

  // Prepare chart data - use stageLabel for display
  const stageChartData = (byStage || []).map((item, idx) => ({
    name: item.stageLabel || item.stage,
    value: item.count,
    color: STAGE_COLORS[idx % STAGE_COLORS.length],
  }));

  const typeChartData = (byType || []).map((item) => ({
    name: DEAL_TYPE_LABELS[item.type] || item.type,
    value: item.value,
    color: TYPE_COLORS[item.type as keyof typeof TYPE_COLORS] || '#95a5a6',
  }));

  const trendsChartData = (trends || []).map((item) => ({
    name: formatDate(item.date),
    pipelineValue: item.pipelineValue,
    wonValue: item.wonValue,
  }));

  const trendsSeries = [
    { dataKey: 'pipelineValue', name: 'Pipeline Value', color: '#3498db' },
    { dataKey: 'wonValue', name: 'Won Value', color: '#27ae60' },
  ];

  // Build subtitle showing date range
  const dateRangeSubtitle = comparisonEnabled
    ? `${formatDateRange(currentRange)} vs ${formatDateRange(previousRange)}`
    : formatDateRange(currentRange);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deal Insights</h1>
            <p className="text-sm text-gray-500 mt-1">
              {dateRangeSubtitle}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['intelligence', 'deals'] })}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Pipeline filter */}
          {!pipelinesLoading && pipelines && pipelines.length > 0 && (
            <Select
              value={selectedPipeline || 'all'}
              onValueChange={(value) => setSelectedPipeline(value === 'all' ? undefined : value)}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Pipelines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Pipelines</SelectItem>
                {pipelines.map((pipeline) => (
                  <SelectItem key={pipeline.id} value={pipeline.id}>
                    {pipeline.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Sales Manager filter */}
          <Select
            value={salesManagerFilter}
            onValueChange={(v) => setSalesManagerFilter(v)}
          >
            <SelectTrigger className="w-[180px]">
              <Users className="h-4 w-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Account Manager" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Account Managers</SelectItem>
              {salesManagers.map((sm) => (
                <SelectItem key={sm.id} value={sm.id}>
                  {sm.name}{sm.isFormer ? ' (Former)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Period Selector with Comparison Toggle */}
          <PeriodSelector
            preset={preset}
            customRange={customRange}
            currentRange={currentRange}
            previousRange={previousRange}
            comparisonEnabled={comparisonEnabled}
            onPresetChange={setPreset}
            onCustomRangeChange={setCustomRange}
            onComparisonChange={setComparisonEnabled}
          />
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Total Pipeline"
          value={summary ? formatCurrency(summary.totalPipelineValue) : undefined}
          icon={<DollarSign className="h-5 w-5" />}
          color="blue"
          loading={summaryLoading}
          trend={comparisonEnabled ? changes?.totalPipelineValue : undefined}
          trendLabel={comparisonEnabled ? 'vs prev' : undefined}
        />
        <MetricCard
          title="Open Deals"
          value={summary?.openDeals}
          icon={<Target className="h-5 w-5" />}
          color="orange"
          loading={summaryLoading}
          trend={comparisonEnabled ? changes?.openDeals : undefined}
          trendLabel={comparisonEnabled ? 'vs prev' : undefined}
        />
        <MetricCard
          title="Won Revenue"
          value={summary ? formatCurrency(summary.totalWonValue) : undefined}
          icon={<Trophy className="h-5 w-5" />}
          color="green"
          loading={summaryLoading}
          trend={comparisonEnabled ? changes?.totalWonValue : undefined}
          trendLabel={comparisonEnabled ? 'vs prev' : undefined}
        />
        <MetricCard
          title="Win Rate"
          value={summary ? `${summary.winRate}%` : undefined}
          icon={<TrendingUp className="h-5 w-5" />}
          color="purple"
          loading={summaryLoading}
          trend={comparisonEnabled ? changes?.winRate : undefined}
          trendLabel={comparisonEnabled ? 'pts' : undefined}
        />
        <MetricCard
          title="Avg Deal Size"
          value={summary ? formatCurrency(summary.avgDealSize) : undefined}
          icon={<Percent className="h-5 w-5" />}
          color="blue"
          loading={summaryLoading}
          trend={comparisonEnabled ? changes?.avgDealSize : undefined}
          trendLabel={comparisonEnabled ? 'vs prev' : undefined}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deals by Stage - Horizontal bar chart for many categories */}
        <ChartContainer
          title="Deals by Stage"
          loading={stageLoading}
          height={Math.max(300, stageChartData.length * 32 + 50)}
        >
          {stageChartData.length > 0 ? (
            <BarChart
              data={stageChartData}
              height={Math.max(250, stageChartData.length * 32)}
              horizontal
              colorByValue
            />
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-500">
              No stage data available
            </div>
          )}
        </ChartContainer>

        {/* Deals by Type */}
        <ChartContainer title="Value by Deal Type" loading={typeLoading} height={300}>
          {typeChartData.length > 0 ? (
            <BarChart
              data={typeChartData}
              height={250}
              colorByValue
            />
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-500">
              No type data available
            </div>
          )}
        </ChartContainer>
      </div>

      {/* Pipeline Trends */}
      <ChartContainer title="Pipeline & Revenue Trends" loading={trendsLoading} height={350}>
        {trendsChartData.length > 0 ? (
          <LineChart
            data={trendsChartData}
            height={300}
            series={trendsSeries}
            showLegend
          />
        ) : (
          <div className="flex items-center justify-center h-[300px] text-gray-500">
            No trend data available
          </div>
        )}
      </ChartContainer>

      {/* Top Deals & Closing Soon */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Deals */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Top Deals by Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : topDeals && topDeals.length > 0 ? (
              <div className="space-y-3">
                {topDeals.slice(0, 5).map((deal) => (
                  <div
                    key={deal.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">{deal.title}</span>
                        {deal.hubspotDealId && (
                          <Badge variant="secondary" className="text-xs">
                            HubSpot
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        {deal.accountName && (
                          <>
                            <Building2 className="h-3.5 w-3.5" />
                            <Link
                              to={`/accounts/${deal.accountId}`}
                              className="hover:text-blue-600 hover:underline"
                            >
                              {deal.accountName}
                            </Link>
                          </>
                        )}
                        <Badge variant="outline" className="ml-2">
                          {deal.stage}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-semibold text-green-600">
                        {formatCurrency(deal.value)}
                      </span>
                      {deal.probability && (
                        <div className="text-xs text-gray-500">{deal.probability}% prob</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No open deals found</div>
            )}
          </CardContent>
        </Card>

        {/* Deals Closing Soon */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              Closing in Next 30 Days
            </CardTitle>
          </CardHeader>
          <CardContent>
            {closingLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : closingSoon && closingSoon.length > 0 ? (
              <div className="space-y-3">
                {closingSoon.slice(0, 5).map((deal) => (
                  <div
                    key={deal.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">{deal.title}</span>
                        {deal.hubspotDealId && (
                          <Badge variant="secondary" className="text-xs">
                            HubSpot
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>{formatDate(deal.closeDate)}</span>
                        <Badge
                          variant={deal.daysUntilClose <= 7 ? 'destructive' : 'outline'}
                          className="ml-1"
                        >
                          {deal.daysUntilClose === 0
                            ? 'Today'
                            : deal.daysUntilClose === 1
                              ? 'Tomorrow'
                              : `${deal.daysUntilClose} days`}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-semibold text-green-600">
                        {formatCurrency(deal.value)}
                      </span>
                      {deal.probability && (
                        <div className="text-xs text-gray-500">{deal.probability}% prob</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No deals closing soon</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Footer */}
      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-sm text-gray-500">Total Deals</p>
              {summaryLoading ? (
                <Skeleton className="h-7 w-16 mx-auto mt-1" />
              ) : (
                <p className="text-xl font-semibold text-gray-900">{summary?.totalDeals || 0}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Won Deals</p>
              {summaryLoading ? (
                <Skeleton className="h-7 w-16 mx-auto mt-1" />
              ) : (
                <p className="text-xl font-semibold text-green-600">{summary?.wonDeals || 0}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">Lost Deals</p>
              {summaryLoading ? (
                <Skeleton className="h-7 w-16 mx-auto mt-1" />
              ) : (
                <p className="text-xl font-semibold text-red-600">{summary?.lostDeals || 0}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500">HubSpot Synced</p>
              {summaryLoading ? (
                <Skeleton className="h-7 w-16 mx-auto mt-1" />
              ) : (
                <p className="text-xl font-semibold text-blue-600">
                  {summary?.hubspotSyncedCount || 0}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
